import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { HERO_IMAGE, HOTEL } from "../../data";
import { MaskLines } from "./primitives";

export const Hero = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);

  useEffect(() => {
    const t = setTimeout(() => setStart(true), 150);
    return () => clearTimeout(t);
  }, []);

  return (
    <section ref={ref} id="top" className="relative h-screen w-full overflow-hidden bg-[#0D120E]" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Hotel Virasat Retreat reception — boutique hotel near Medanta Hospital, Patna"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />

      <div className="relative z-10 h-full mx-auto max-w-[1400px] px-5 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={start ? { opacity: 1 } : {}}
          transition={{ delay: 0.25, duration: 1 }}
          className="overline text-[#B89965] mb-5"
        >
          Patna · Bihar · Beside Medanta Hospital
        </motion.p>

        <h1 className="font-display text-[#F5F2ED] font-light leading-[0.92] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
          <MaskLines start={start} lines={["Hotel Virasat", "Retreat"]} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={start ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 1 }}
          className="mt-6 font-display italic text-2xl md:text-3xl text-[#B89965]"
          data-testid="hero-tagline"
        >
          Traditional Hospitality · Modern Facilities
        </motion.p>

        <div className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.9, duration: 1 }}
            className="text-[#F5F2ED]/85 max-w-md text-base md:text-lg leading-relaxed"
          >
            A 16-room heritage boutique stay in the heart of Patna — where legacy hospitality meets the calm of a rooftop garden.
          </motion.p>
          <motion.a
            href="#enquiry"
            initial={{ opacity: 0, y: 20 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 1.1, duration: 1 }}
            data-testid="hero-book-cta"
            className="btn-fill text-[#F5F2ED] hover:text-[#0D120E] border border-[#F5F2ED]/60 rounded-full px-8 py-4 inline-flex items-center gap-2 self-start"
          >
            Check Availability
          </motion.a>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={start ? { opacity: 1 } : {}}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-10 z-10 flex items-center gap-2 text-[#F5F2ED]/70 text-xs"
      >
        <span className="overline">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
